import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ActionType } from '../shared/types';
import { hapticImpact } from '../lib/telegram';

interface ActionButtonsProps {
  legal: { types: ActionType[]; minRaiseTo: number; maxRaiseTo: number; callAmount: number };
  bigBlind: number;
  onAction: (type: ActionType, amount?: number) => void;
}

export function ActionButtons({ legal, bigBlind, onAction }: ActionButtonsProps) {
  const [raiseTo, setRaiseTo] = useState(legal.minRaiseTo);
  const [showSlider, setShowSlider] = useState(false);

  useEffect(() => {
    setRaiseTo(legal.minRaiseTo);
    setShowSlider(false);
  }, [legal.minRaiseTo, legal.maxRaiseTo]);

  if (legal.types.length === 0) return null;

  const canRaise = legal.types.includes('bet') || legal.types.includes('raise');
  const raiseLabel = legal.types.includes('bet') ? 'Ставка' : 'Рейз';

  function act(type: ActionType, amount?: number) {
    hapticImpact('medium');
    onAction(type, amount);
    setShowSlider(false);
  }

  return (
    <div className="w-full">
      {showSlider && canRaise && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-2 px-3 py-2 rounded-xl bg-ink-800/90 border border-gold-500/20"
        >
          <div className="flex justify-between text-xs text-white/60 mb-1">
            <span>{legal.minRaiseTo.toLocaleString('ru-RU')}</span>
            <span className="text-gold-400 font-semibold text-sm">{raiseTo.toLocaleString('ru-RU')}</span>
            <span>{legal.maxRaiseTo.toLocaleString('ru-RU')}</span>
          </div>
          <input
            type="range"
            min={legal.minRaiseTo}
            max={legal.maxRaiseTo}
            step={Math.max(1, bigBlind)}
            value={raiseTo}
            onChange={(e) => setRaiseTo(Number(e.target.value))}
            className="w-full accent-emerald-400"
          />
          <div className="flex gap-2 mt-2">
            {[0.5, 0.75, 1].map((frac) => {
              const val = Math.min(
                legal.maxRaiseTo,
                Math.round((legal.minRaiseTo + frac * (legal.maxRaiseTo - legal.minRaiseTo)) / bigBlind) * bigBlind,
              );
              return (
                <button
                  key={frac}
                  onClick={() => setRaiseTo(val)}
                  className="flex-1 py-1 rounded-lg bg-ink-700 text-white/70 text-xs hover:bg-ink-600"
                >
                  {Math.round(frac * 100)}%
                </button>
              );
            })}
            <button
              onClick={() => setRaiseTo(legal.maxRaiseTo)}
              className="flex-1 py-1 rounded-lg bg-rose-900/60 text-rose-300 text-xs hover:bg-rose-900"
            >
              Ва-банк
            </button>
          </div>
        </motion.div>
      )}

      <div className="flex gap-2">
        {legal.types.includes('fold') && (
          <ActionBtn label="Фолд" tone="danger" onClick={() => act('fold')} />
        )}
        {legal.types.includes('check') && (
          <ActionBtn label="Чек" tone="neutral" onClick={() => act('check')} />
        )}
        {legal.types.includes('call') && (
          <ActionBtn
            label={`Колл ${legal.callAmount.toLocaleString('ru-RU')}`}
            tone="primary"
            onClick={() => act('call')}
          />
        )}
        {canRaise && !showSlider && (
          <ActionBtn label={raiseLabel} tone="gold" onClick={() => setShowSlider(true)} />
        )}
        {canRaise && showSlider && (
          <ActionBtn
            label={`${raiseLabel} до ${raiseTo.toLocaleString('ru-RU')}`}
            tone="gold"
            onClick={() => act(legal.types.includes('bet') ? 'bet' : 'raise', raiseTo)}
          />
        )}
      </div>
    </div>
  );
}

function ActionBtn({
  label,
  tone,
  onClick,
}: {
  label: string;
  tone: 'danger' | 'neutral' | 'primary' | 'gold';
  onClick: () => void;
}) {
  const toneClass = {
    danger: 'bg-rose-900/70 text-rose-200 active:bg-rose-800',
    neutral: 'bg-ink-700 text-white/80 active:bg-ink-600',
    primary: 'bg-emerald-600 text-white active:bg-emerald-500',
    gold: 'text-ink-950 font-bold active:brightness-95 bg-cover bg-center',
  }[tone];
  const goldStyle =
    tone === 'gold'
      ? { backgroundImage: `linear-gradient(to bottom, rgba(247,226,164,0.85), rgba(199,154,47,0.9)), url(/textures/gold-foil.jpg)` }
      : undefined;

  return (
    <motion.button
      whileTap={{ scale: 0.95 }}
      onClick={onClick}
      style={goldStyle}
      className={`flex-1 py-3 rounded-xl text-sm font-semibold shadow-card ${toneClass}`}
    >
      {label}
    </motion.button>
  );
}
